import React, { useState, useMemo } from "react";
import { exportTableToExcel } from "../../utils/exportData"; // Updated import

const fiscalMonthOrder = [
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
];

const isNumeric = (val) => !isNaN(val) && !isNaN(parseFloat(val));

function getModifierKey() {
  let isMac = false;
  if (navigator.userAgentData && navigator.userAgentData.platform) {
    isMac = navigator.userAgentData.platform === 'macOS';
  } else {
    isMac = /Mac/i.test(navigator.userAgent);
  }
  return isMac ? 'Cmd' : 'Ctrl';
}
function getDisplayName(heading) {
  const lowerHeading = heading.toLowerCase();

  // Handle specific cases
  if (
    lowerHeading.includes("monthname") ||
    lowerHeading.includes("month_name")
  ) {
    return "Month";
  }
  if (
    lowerHeading.includes("statename") ||
    lowerHeading.includes("state_name")
  ) {
    return "State";
  }
  if (
    lowerHeading.includes("districtname") ||
    lowerHeading.includes("district_name")
  ) {
    return "District";
  }

  // Handle camelCase and snake_case conversion
  const cleaned = heading
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase())
    .replace(/\s+/g, " ")
    .trim();

  return cleaned;
}

export default function SortableTable({
  headings = [],
  rawData = [],
  isNarrow = false,
}) {
  const [sortConfig, setSortConfig] = useState([]);
  const containerWidth = isNarrow ? "w-full lg:w-[40%]" : "w-full";

  const sortedData = useMemo(() => {
    const sorted = [...rawData];

    if (sortConfig.length > 0) {
      sorted.sort((a, b) => {
        for (const config of sortConfig) {
          const valA = a[config.key];
          const valB = b[config.key];

          let comparison = 0;

          if (config.key?.startsWith("Month")) {
            const indexA = fiscalMonthOrder.indexOf(valA);
            const indexB = fiscalMonthOrder.indexOf(valB);
            comparison = indexA - indexB;
          } else if (isNumeric(valA) && isNumeric(valB)) {
            comparison = parseFloat(valA) - parseFloat(valB);
          } else {
            comparison = valA?.toString().localeCompare(valB?.toString());
          }

          if (config.direction === "desc") {
            comparison = -comparison;
          }

          if (comparison !== 0) {
            return comparison;
          }
        }

        return 0;
      });
    }

    return sorted;
  }, [rawData, sortConfig]);

  // Handle Excel download
  const handleExcelDownload = () => {
    try {
      const filename = `DataTable`;
      exportTableToExcel(headings, sortedData, filename);
    } catch (error) {
      console.error("Failed to download Excel file:", error);
    }
  };

  return (
    <div
      className={`${containerWidth} h-[min(700px,70vh)] min-h-[320px] min-w-0 flex flex-col px-[10px] py-[10px] text-center border rounded-lg bg-gray-50 shadow-md overflow-hidden relative`}
    >
      {/* Header */}
      <div className="flex justify-center items-center mb-2 flex-shrink-0">
        <h3 className="text-lg font-semibold">Data Table</h3>
      </div>

      <div className="text-xs text-gray-600 mb-2 flex-shrink-0">
        Click column to sort '{getModifierKey()}+Click' on the column for
        multi-column sort
      </div>

      <div className="flex-1 overflow-auto">
        <table className="table-auto w-full text-base">
          <thead className="sticky top-0 bg-[#d5d5d5]">
            <tr>
              {headings.map((item, idx) => (
                <th
                  key={idx}
                  scope="col"
                  tabIndex={0}
                  className="px-3 py-2 border font-semibold text-xs cursor-pointer select-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#005856]"
                  aria-sort={(() => {
                    const cfg = sortConfig.find((c) => c.key === item);
                    if (!cfg) return "none";
                    return cfg.direction === "asc" ? "ascending" : "descending";
                  })()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.currentTarget.click();
                    }
                  }}
                  onClick={(e) => {
                    const isCtrlClick = e.ctrlKey || e.metaKey;

                    setSortConfig((prev) => {
                      const existingIndex = prev.findIndex(
                        (config) => config.key === item
                      );

                      if (isCtrlClick) {
                        if (existingIndex >= 0) {
                          const existing = prev[existingIndex];
                          if (existing.direction === "asc") {
                            const newConfig = [...prev];
                            newConfig[existingIndex] = {
                              key: item,
                              direction: "desc",
                            };
                            return newConfig;
                          } else {
                            return prev.filter(
                              (_, index) => index !== existingIndex
                            );
                          }
                        } else {
                          return [...prev, { key: item, direction: "asc" }];
                        }
                      } else {
                        if (existingIndex >= 0 && prev.length === 1) {
                          const existing = prev[0];
                          return [
                            {
                              key: item,
                              direction:
                                existing.direction === "asc" ? "desc" : "asc",
                            },
                          ];
                        } else {
                          return [{ key: item, direction: "asc" }];
                        }
                      }
                    });
                  }}
                >
                  <div className="flex items-center justify-center gap-1">
                    {getDisplayName(item)}
                    <span className="text-[10px] text-gray-500 flex items-center">
                      {(() => {
                        const sortIndex = sortConfig.findIndex(
                          (config) => config.key === item
                        );
                        if (sortIndex >= 0) {
                          const config = sortConfig[sortIndex];
                          return (
                            <>
                              {config.direction === "asc" ? "⭡" : "⭣"}
                              {sortConfig.length > 1 && (
                                <span className="ml-1 text-[8px] bg-blue-500 text-white rounded-full w-3 h-3 flex items-center justify-center">
                                  {sortIndex + 1}
                                </span>
                              )}
                            </>
                          );
                        }
                        return "⭥";
                      })()}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className={rowIdx % 2 === 0 ? "bg-white" : "bg-gray-100"}
              >
                {headings.map((header, colIdx) => (
                  <td key={colIdx} className="px-3 py-2 border text-xs">
                    {row[header] ?? ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Excel Download Icon - Bottom Right Corner */}
      <div className="absolute bottom-3 right-4 group">
        <button
          type="button"
          onClick={handleExcelDownload}
          aria-label="Download table data as Excel"
          className={`
            ${
              rawData.length === 0
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gray-600 hover:bg-gray-700 hover:scale-110 active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#005856]"
            } 
            text-white p-2 rounded-full shadow-lg transition-all duration-200 
            flex items-center justify-center
          `}
          disabled={rawData.length === 0}
          title="Download as Excel"
        >
          {/* Download arrow with Excel styling */}
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </button>

        {/* Enhanced Tooltip */}
        <div className="absolute bottom-full right-0 mb-3 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap z-50 shadow-xl">
          <div className="flex items-center space-x-2">
            <svg
              className="w-4 h-4 text-green-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V4a2 2 0 00-2-2H4zm2 3h8v2H6V5zm0 4h8v2H6V9zm0 4h8v2H6v-2z" />
            </svg>
            <span className="font-medium">Download Excel</span>
          </div>
          {/* Tooltip Arrow */}
          <div className="absolute top-full right-4 w-0 h-0 border-l-2 border-r-2 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
        </div>
      </div>
    </div>
  );
}
